"use client";

import { useCallback } from "react";
import { useAnalyticsContext, TrackingEvent, ProductType } from "@/analytics";
import { buildHotelEcommerceItem } from "@/analytics/services/hotel/utils";

/**
 * Favorilere ekle (add_to_wishlist) event'ini tetikler.
 * Landing, Search, Map kartları ve Detay Header'da kullanılır.
 * Veri olmayan alanlar buildHotelEcommerceItem / normalizeHotelEcommerceForDataLayer ile null basar.
 *
 * @param {Object} params
 * @param {Object} params.product - buildHotelEcommerceItem uyumlu otel objesi (mapHotelItem / mapLandingHotelItem / productForViewItem)
 * @param {string} [params.itemListId]
 * @param {string} [params.itemListName]
 * @param {Object} [params.passengers] - { guest_total, guest_adult_number, guest_children_number }
 * @param {string} [params.currency]
 * @param {number} [params.value]
 * @returns {{ trackAddToWishlist: () => void }}
 */
export function useHotelAddToWishlistAnalytics({
  product,
  itemListId = "hotel_search",
  itemListName = "Hotel Search",
  passengers,
  currency = "TRY",
  value,
}) {
  const { dispatch: trackingDispatch } = useAnalyticsContext();

  const trackAddToWishlist = useCallback(() => {
    if (!product) return;
    const items = buildHotelEcommerceItem({
      product,
      index: 0,
      item_list_id: itemListId,
      item_list_name: itemListName,
      cross_product_promotion: undefined,
    });
    if (!items?.length) return;

    const firstItem = items[0];
    const ecommerce = {
      currency: currency ?? firstItem?.currency ?? "TRY",
      ...(typeof value === "number" && Number.isFinite(value) ? { value } : {}),
      passengers: passengers ?? null,
      items,
    };

    trackingDispatch({
      type: TrackingEvent.ADD_TO_WISHLIST,
      payload: { productType: ProductType.HOTEL, ecommerce },
    });
  }, [product, itemListId, itemListName, passengers, currency, value, trackingDispatch]);

  return { trackAddToWishlist };
}
